import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Lock, Unlock, CheckCircle2, Loader2, Mail, Phone, Users, Clock, Search, Plus, AlertTriangle, Building2, Trash2, UserX } from 'lucide-react';
import { Sidebar, Topbar } from './Dashboard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import { auth, admin, getStoredUser } from '../api';

const STATUS_META = {
  trial: { label: 'Trial', cls: 'bg-sky-50 text-sky-800 border-sky-200' },
  active: { label: 'Paid · Active', cls: 'bg-emerald-50 text-emerald-800 border-emerald-200' },
  locked: { label: 'Locked', cls: 'bg-red-50 text-red-800 border-red-200' },
};

function StatusPill({ status }) {
  const meta = STATUS_META[status] || STATUS_META.trial;
  return <span className={`inline-flex items-center text-[11px] font-semibold px-2.5 py-1 rounded-full border ${meta.cls}`} data-testid={`firm-status-${status}`}>{meta.label}</span>;
}

function fmtDate(iso) {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return '—'; }
}

function TrialBadge({ status, daysLeft }) {
  if (status === 'active') return <span className="text-xs text-emerald-700 font-medium">On paid plan</span>;
  if (status === 'locked') return <span className="text-xs text-red-700 font-medium">Access revoked</span>;
  if (daysLeft == null) return <span className="text-xs text-stone-500">—</span>;
  if (daysLeft === 0) return <span className="text-xs text-amber-700 font-medium">Trial ended — payment due</span>;
  if (daysLeft <= 3) return <span className="text-xs text-amber-700 font-medium">{daysLeft} day{daysLeft === 1 ? '' : 's'} left</span>;
  return <span className="text-xs text-sky-700 font-medium">{daysLeft} days left</span>;
}

export default function Access() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState(getStoredUser());
  const [loading, setLoading] = useState(true);
  const [firms, setFirms] = useState([]);
  const [q, setQ] = useState('');
  const [filter, setFilter] = useState('all');
  const [acting, setActing] = useState({}); // firmId → bool
  const [lockDialog, setLockDialog] = useState(null); // firm object
  const [lockReason, setLockReason] = useState('Payment outstanding — contact your administrator.');
  const [extendDialog, setExtendDialog] = useState(null);
  const [extendDays, setExtendDays] = useState(14);
  const [deleteDialog, setDeleteDialog] = useState(null); // firm being deleted
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [membersDialog, setMembersDialog] = useState(null); // firm whose members we're viewing
  const [membersList, setMembersList] = useState([]);
  const [membersLoading, setMembersLoading] = useState(false);
  const [deletingMemberId, setDeletingMemberId] = useState(null);

  useEffect(() => {
    if (!localStorage.getItem('ad_token')) { nav('/login'); return; }
    (async () => {
      try {
        const me = await auth.me().catch(() => null);
        if (me) setUser(me);
        if (me && !me.isPlatformAdmin) { nav('/dashboard'); return; }
        await reload();
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line
  }, []);

  const reload = async () => {
    try {
      const res = await admin.listFirms();
      setFirms(res.firms || []);
    } catch (err) {
      toast({ title: 'Could not load firms', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    return firms.filter((f) => {
      if (filter !== 'all' && f.accessStatus !== filter) return false;
      if (!query) return true;
      return [f.firmName, f.ownerName, f.ownerEmail, f.ownerPhone].some((v) => (v || '').toLowerCase().includes(query));
    });
  }, [firms, q, filter]);

  const kpis = useMemo(() => {
    const total = firms.length;
    const trial = firms.filter((f) => f.accessStatus === 'trial').length;
    const active = firms.filter((f) => f.accessStatus === 'active').length;
    const locked = firms.filter((f) => f.accessStatus === 'locked').length;
    const trialEndingSoon = firms.filter((f) => f.accessStatus === 'trial' && (f.daysLeftInTrial ?? 999) <= 3).length;
    return { total, trial, active, locked, trialEndingSoon };
  }, [firms]);

  const setBusy = (fid, v) => setActing((s) => ({ ...s, [fid]: v }));

  const doLock = async () => {
    if (!lockDialog) return;
    setBusy(lockDialog.firmId, true);
    try {
      await admin.lock(lockDialog.firmId, lockReason);
      toast({ title: 'Firm locked', description: `${lockDialog.firmName || lockDialog.ownerEmail} can no longer sign in.` });
      setLockDialog(null);
      await reload();
    } catch (err) {
      toast({ title: 'Lock failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusy(lockDialog.firmId, false); }
  };

  const doUnlock = async (f) => {
    setBusy(f.firmId, true);
    try {
      await admin.unlock(f.firmId);
      toast({ title: 'Firm unlocked', description: `${f.firmName || f.ownerEmail} now has access.` });
      await reload();
    } catch (err) {
      toast({ title: 'Unlock failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusy(f.firmId, false); }
  };

  const doActivate = async (f) => {
    setBusy(f.firmId, true);
    try {
      await admin.activate(f.firmId);
      toast({ title: 'Marked as paid', description: `${f.firmName || f.ownerEmail} is now on an active plan.` });
      await reload();
    } catch (err) {
      toast({ title: 'Activation failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusy(f.firmId, false); }
  };

  const doExtend = async () => {
    if (!extendDialog) return;
    setBusy(extendDialog.firmId, true);
    try {
      await admin.extendTrial(extendDialog.firmId, Number(extendDays) || 7);
      toast({ title: 'Trial extended', description: `${extendDays} days added to ${extendDialog.firmName || extendDialog.ownerEmail}.` });
      setExtendDialog(null);
      await reload();
    } catch (err) {
      toast({ title: 'Extend failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusy(extendDialog.firmId, false); }
  };

  const openMembers = async (f) => {
    setMembersDialog(f);
    setMembersLoading(true);
    setMembersList([]);
    try {
      const res = await admin.listMembers(f.firmId);
      setMembersList(res.members || []);
    } catch (err) {
      toast({ title: 'Could not load profiles', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
      setMembersDialog(null);
    } finally { setMembersLoading(false); }
  };

  const doDeleteMember = async (m) => {
    if (!membersDialog) return;
    if (!window.confirm(`Permanently delete ${m.name || m.email}? This cannot be undone.`)) return;
    setDeletingMemberId(m.id);
    try {
      await admin.deleteMember(membersDialog.firmId, m.id);
      toast({ title: 'Profile deleted', description: `${m.name || m.email} removed from the firm.` });
      setMembersList((prev) => prev.filter((x) => x.id !== m.id));
      await reload();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setDeletingMemberId(null); }
  };

  const openDelete = (f) => {
    setDeleteDialog(f);
    setDeleteConfirmText('');
  };

  const deleteExpected = (deleteDialog?.firmName || deleteDialog?.ownerEmail || '').trim();
  const deleteConfirmValid = deleteConfirmText.trim().toLowerCase() === deleteExpected.toLowerCase() && deleteExpected.length > 0;

  const doDeleteFirm = async () => {
    if (!deleteDialog || !deleteConfirmValid) return;
    setBusy(deleteDialog.firmId, true);
    try {
      const res = await admin.deleteFirm(deleteDialog.firmId);
      const total = Object.values(res.cascade || {}).reduce((a, b) => a + b, 0);
      toast({ title: 'Firm deleted', description: `${deleteDialog.firmName || deleteDialog.ownerEmail} removed. ${total} record${total === 1 ? '' : 's'} cleaned up.` });
      setDeleteDialog(null);
      setDeleteConfirmText('');
      await reload();
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusy(deleteDialog.firmId, false); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 grid place-items-center">
        <div className="flex items-center gap-2 text-stone-600"><Loader2 className="animate-spin" size={18}/> Loading subscriber list…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar activeLabel="Access"/>
      <div className="flex-1 min-w-0 flex flex-col">
        <Topbar user={user} firm={user?.firm}/>
        <main className="flex-1 p-4 lg:p-8 space-y-6">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <div className="flex items-center gap-2 text-sky-700 text-[11px] uppercase tracking-widest font-semibold"><Shield size={13}/> Accu Desk Access · Platform admin</div>
              <h1 className="mt-1 font-serif text-3xl font-semibold text-stone-900">Subscriber control room</h1>
              <p className="mt-1 text-stone-600 max-w-2xl text-sm">Every firm that signs up appears here on day one. Call them with the numbers below for monthly payment, mark them as paid, extend their trial, or lock access until they settle up.</p>
            </div>
          </div>

          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {[
              { label: 'Total firms', value: kpis.total, Icon: Building2, tone: 'bg-stone-100 text-stone-700' },
              { label: 'On trial', value: kpis.trial, Icon: Clock, tone: 'bg-sky-50 text-sky-800' },
              { label: 'Paid · active', value: kpis.active, Icon: CheckCircle2, tone: 'bg-emerald-50 text-emerald-800' },
              { label: 'Locked', value: kpis.locked, Icon: Lock, tone: 'bg-red-50 text-red-800' },
              { label: 'Trial ending ≤ 3 days', value: kpis.trialEndingSoon, Icon: AlertTriangle, tone: 'bg-amber-50 text-amber-800' },
            ].map((k) => (
              <div key={k.label} className="rounded-xl bg-white border border-stone-200 p-4" data-testid={`access-kpi-${k.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
                <div className={`inline-flex items-center justify-center h-9 w-9 rounded-lg ${k.tone}`}><k.Icon size={16}/></div>
                <div className="mt-3 text-2xl font-serif font-semibold text-stone-900 tabular-nums">{k.value}</div>
                <div className="text-xs text-stone-500">{k.label}</div>
              </div>
            ))}
          </div>

          {/* Filters */}
          <div className="rounded-xl bg-white border border-stone-200 p-4 flex items-center gap-3 flex-wrap">
            <div className="relative flex-1 min-w-[220px]">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search by firm, owner name, email or phone…"
                className="pl-9 h-10"
                data-testid="access-search"
              />
            </div>
            <div className="flex items-center gap-1 bg-stone-100 rounded-lg p-1">
              {[
                ['all', 'All'],
                ['trial', 'Trial'],
                ['active', 'Paid'],
                ['locked', 'Locked'],
              ].map(([k, lbl]) => (
                <button
                  key={k}
                  onClick={() => setFilter(k)}
                  className={`px-3 h-8 text-xs font-medium rounded-md transition-colors ${filter === k ? 'bg-white text-stone-900 shadow-sm' : 'text-stone-600 hover:text-stone-900'}`}
                  data-testid={`access-filter-${k}`}
                >
                  {lbl}
                </button>
              ))}
            </div>
          </div>

          {/* Firms list */}
          <div className="rounded-xl bg-white border border-stone-200 overflow-hidden" data-testid="access-firms-list">
            <div className="hidden lg:grid grid-cols-[1.4fr_1.4fr_1.1fr_0.9fr_1.6fr] gap-4 px-5 py-3 bg-stone-50 border-b border-stone-200 text-[11px] uppercase tracking-wider text-stone-500 font-semibold">
              <div>Firm · Owner</div>
              <div>Contact</div>
              <div>Status</div>
              <div>Trial / Signup</div>
              <div className="text-right">Actions</div>
            </div>
            {filtered.length === 0 && (
              <div className="p-12 text-center">
                <div className="mx-auto h-12 w-12 rounded-full bg-stone-100 grid place-items-center text-stone-500 mb-3"><Building2 size={20}/></div>
                <div className="font-serif text-lg text-stone-900">No firms match that filter</div>
                <div className="text-sm text-stone-500 mt-1">Try a different search or switch the status filter above.</div>
              </div>
            )}
            {filtered.map((f) => {
              const busy = !!acting[f.firmId];
              const locked = f.accessStatus === 'locked';
              return (
                <div
                  key={f.firmId}
                  className="grid lg:grid-cols-[1.4fr_1.4fr_1.1fr_0.9fr_1.6fr] gap-4 px-5 py-4 border-b border-stone-100 last:border-b-0 hover:bg-stone-50/70"
                  data-testid={`access-firm-row-${f.firmId}`}
                >
                  <div className="min-w-0">
                    <div className="font-semibold text-stone-900 truncate flex items-center gap-2">
                      {f.firmName || <span className="italic text-stone-500">Unnamed firm</span>}
                      {f.isPlatformAdminFirm && <span className="text-[9px] uppercase tracking-wider text-sky-700 bg-sky-50 px-1.5 py-0.5 rounded">You</span>}
                    </div>
                    <div className="text-xs text-stone-500 truncate mt-0.5">{f.ownerName || '—'}</div>
                    <div className="text-[11px] text-stone-400 mt-0.5 flex items-center gap-2"><Users size={10}/> {f.memberCount} member{f.memberCount === 1 ? '' : 's'} · {f.clientCount} client{f.clientCount === 1 ? '' : 's'}</div>
                  </div>
                  <div className="min-w-0 text-sm space-y-0.5">
                    {f.ownerEmail && (
                      <a href={`mailto:${f.ownerEmail}`} className="flex items-center gap-1.5 text-stone-700 hover:text-emerald-800 truncate" data-testid={`access-email-${f.firmId}`}>
                        <Mail size={12} className="flex-shrink-0"/> <span className="truncate">{f.ownerEmail}</span>
                      </a>
                    )}
                    {f.ownerPhone ? (
                      <a href={`tel:${f.ownerPhone.replace(/\s/g, '')}`} className="flex items-center gap-1.5 text-stone-700 hover:text-emerald-800" data-testid={`access-phone-${f.firmId}`}>
                        <Phone size={12}/> <span className="tabular-nums">{f.ownerPhone}</span>
                      </a>
                    ) : (
                      <div className="flex items-center gap-1.5 text-stone-400 text-xs"><Phone size={12}/> No number on file</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-1">
                    <StatusPill status={f.accessStatus}/>
                    <TrialBadge status={f.accessStatus} daysLeft={f.daysLeftInTrial}/>
                    {locked && f.lockedReason && (
                      <div className="text-[10px] text-red-700 italic truncate" title={f.lockedReason}>“{f.lockedReason}”</div>
                    )}
                  </div>
                  <div className="text-xs text-stone-600 space-y-0.5">
                    <div>Trial ends: <span className="font-medium text-stone-900">{fmtDate(f.trialEndsAt)}</span></div>
                    <div className="text-stone-400">Signed up {fmtDate(f.trialStartsAt)}</div>
                  </div>
                  <div className="flex flex-wrap gap-1.5 lg:justify-end">
                    {f.isPlatformAdminFirm ? (
                      <span className="text-xs text-stone-400 italic self-center">Your own firm · protected</span>
                    ) : (
                      <>
                        <Button size="sm" variant="ghost" onClick={() => openMembers(f)} disabled={busy} className="h-8 text-xs text-stone-600 hover:text-stone-900" data-testid={`access-members-${f.firmId}`}>
                          <Users size={13} className="mr-1"/> Profiles
                        </Button>
                        {!locked && f.accessStatus === 'trial' && (
                          <Button size="sm" variant="outline" onClick={() => doActivate(f)} disabled={busy} className="h-8 text-xs" data-testid={`access-mark-paid-${f.firmId}`}>
                            <CheckCircle2 size={13} className="mr-1"/> Mark paid
                          </Button>
                        )}
                        {!locked && (
                          <Button size="sm" variant="outline" onClick={() => { setExtendDialog(f); setExtendDays(14); }} disabled={busy} className="h-8 text-xs" data-testid={`access-extend-${f.firmId}`}>
                            <Plus size={13} className="mr-1"/> Extend trial
                          </Button>
                        )}
                        {locked ? (
                          <Button size="sm" onClick={() => doUnlock(f)} disabled={busy} className="h-8 text-xs bg-emerald-800 hover:bg-emerald-900 text-white" data-testid={`access-unlock-${f.firmId}`}>
                            {busy ? <Loader2 size={13} className="mr-1 animate-spin"/> : <Unlock size={13} className="mr-1"/>} Unlock
                          </Button>
                        ) : (
                          <Button size="sm" variant="outline" onClick={() => { setLockDialog(f); setLockReason('Payment outstanding — contact your administrator.'); }} disabled={busy} className="h-8 text-xs border-red-200 text-red-700 hover:bg-red-50" data-testid={`access-lock-${f.firmId}`}>
                            <Lock size={13} className="mr-1"/> Lock
                          </Button>
                        )}
                        <Button size="sm" variant="outline" onClick={() => openDelete(f)} disabled={busy} className="h-8 text-xs border-red-300 text-red-700 hover:bg-red-50" data-testid={`access-delete-${f.firmId}`}>
                          <Trash2 size={13} className="mr-1"/> Delete
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </main>
      </div>

      {/* Lock dialog */}
      <Dialog open={!!lockDialog} onOpenChange={(o) => !o && setLockDialog(null)}>
        <DialogContent className="sm:max-w-[460px]" data-testid="access-lock-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Lock size={16} className="text-red-700"/> Lock {lockDialog?.firmName || lockDialog?.ownerEmail}</DialogTitle>
            <DialogDescription>The firm will immediately lose access. All users there will see a lock screen next time they try to sign in with the reason you enter below.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="lock-reason">Message shown to the firm</Label>
              <Textarea
                id="lock-reason"
                rows={3}
                value={lockReason}
                onChange={(e) => setLockReason(e.target.value)}
                className="mt-1.5"
                data-testid="access-lock-reason"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLockDialog(null)}>Cancel</Button>
            <Button onClick={doLock} className="bg-red-700 hover:bg-red-800 text-white" data-testid="access-lock-confirm">
              <Lock size={14} className="mr-1.5"/> Lock access
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Extend dialog */}
      <Dialog open={!!extendDialog} onOpenChange={(o) => !o && setExtendDialog(null)}>
        <DialogContent className="sm:max-w-[400px]" data-testid="access-extend-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Plus size={16} className="text-sky-700"/> Extend trial</DialogTitle>
            <DialogDescription>Grant <strong>{extendDialog?.firmName || extendDialog?.ownerEmail}</strong> extra trial days from today.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label htmlFor="extend-days">Days to add</Label>
              <Input
                id="extend-days"
                type="number"
                min={1}
                max={365}
                value={extendDays}
                onChange={(e) => setExtendDays(e.target.value)}
                className="mt-1.5"
                data-testid="access-extend-days"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExtendDialog(null)}>Cancel</Button>
            <Button onClick={doExtend} className="bg-emerald-800 hover:bg-emerald-900 text-white" data-testid="access-extend-confirm">
              Add {extendDays || 0} days
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Profiles / members dialog */}
      <Dialog open={!!membersDialog} onOpenChange={(o) => { if (!o) { setMembersDialog(null); setMembersList([]); } }}>
        <DialogContent className="sm:max-w-[560px]" data-testid="access-members-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Users size={16} className="text-stone-700"/> Profiles in {membersDialog?.firmName || membersDialog?.ownerEmail}</DialogTitle>
            <DialogDescription>Remove individual teammates one at a time. The firm owner is protected — delete the whole firm to remove them.</DialogDescription>
          </DialogHeader>
          <div className="max-h-80 overflow-y-auto -mx-2 px-2">
            {membersLoading ? (
              <div className="py-10 flex items-center justify-center gap-2 text-stone-500 text-sm"><Loader2 size={14} className="animate-spin"/> Loading profiles…</div>
            ) : membersList.length === 0 ? (
              <div className="py-10 text-center text-stone-500 text-sm">No profiles found.</div>
            ) : (
              <ul className="divide-y divide-stone-100">
                {membersList.map((m) => {
                  const protectedProfile = m.firmRole === 'owner' || m.isPlatformAdmin;
                  return (
                    <li key={m.id} className="py-3 flex items-start gap-3" data-testid={`access-member-${m.id}`}>
                      <div className="h-8 w-8 rounded-full bg-stone-100 text-stone-700 grid place-items-center text-[11px] font-semibold flex-shrink-0">
                        {(m.name || m.email || '?').split(' ').map((s) => s[0]).slice(0, 2).join('').toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <div className="font-medium text-stone-900 text-sm truncate">{m.name || m.email}</div>
                          {m.firmRole === 'owner' && <span className="text-[9px] uppercase tracking-wider text-sky-700 bg-sky-50 px-1.5 py-0.5 rounded">Owner</span>}
                          {m.isPlatformAdmin && <span className="text-[9px] uppercase tracking-wider text-purple-700 bg-purple-50 px-1.5 py-0.5 rounded">Admin</span>}
                        </div>
                        <div className="text-xs text-stone-500 truncate">{m.email}</div>
                        {m.phone && <div className="text-[11px] text-stone-500 tabular-nums">{m.phone}</div>}
                      </div>
                      {protectedProfile ? (
                        <span className="text-[11px] text-stone-400 italic self-center flex-shrink-0">Protected</span>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => doDeleteMember(m)}
                          disabled={deletingMemberId === m.id}
                          className="h-8 text-xs border-red-200 text-red-700 hover:bg-red-50 flex-shrink-0"
                          data-testid={`access-member-delete-${m.id}`}
                        >
                          {deletingMemberId === m.id ? <Loader2 size={12} className="mr-1 animate-spin"/> : <UserX size={12} className="mr-1"/>} Delete
                        </Button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setMembersDialog(null); setMembersList([]); }}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete firm dialog */}
      <Dialog open={!!deleteDialog} onOpenChange={(o) => { if (!o) { setDeleteDialog(null); setDeleteConfirmText(''); } }}>
        <DialogContent className="sm:max-w-[500px]" data-testid="access-delete-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-800"><Trash2 size={16}/> Permanently delete this firm</DialogTitle>
            <DialogDescription>
              This will erase every profile, client, task, timesheet, invoice and chat message belonging to this firm. It cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-900">
              <div className="font-semibold flex items-center gap-1.5"><AlertTriangle size={14}/> You are deleting</div>
              <div className="mt-1 pl-5">
                <div className="text-base font-serif font-semibold">{deleteDialog?.firmName || <span className="italic">Unnamed firm</span>}</div>
                <div className="text-xs text-red-800/80 mt-0.5">Owner: {deleteDialog?.ownerName || '—'} ({deleteDialog?.ownerEmail || '—'})</div>
                <div className="text-xs text-red-800/80">{deleteDialog?.memberCount} profile{deleteDialog?.memberCount === 1 ? '' : 's'} · {deleteDialog?.clientCount} client{deleteDialog?.clientCount === 1 ? '' : 's'}</div>
              </div>
            </div>
            <div>
              <Label htmlFor="delete-confirm">
                Type <span className="font-mono text-red-800">{deleteExpected || '—'}</span> to confirm
              </Label>
              <Input
                id="delete-confirm"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                autoComplete="off"
                className="mt-1.5"
                data-testid="access-delete-confirm-input"
                placeholder={deleteExpected}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDeleteDialog(null); setDeleteConfirmText(''); }}>Cancel</Button>
            <Button
              onClick={doDeleteFirm}
              disabled={!deleteConfirmValid || !!acting[deleteDialog?.firmId]}
              className="bg-red-700 hover:bg-red-800 text-white disabled:opacity-40"
              data-testid="access-delete-confirm-btn"
            >
              {acting[deleteDialog?.firmId] ? <Loader2 size={14} className="mr-1.5 animate-spin"/> : <Trash2 size={14} className="mr-1.5"/>} Delete firm forever
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
